import React, { useEffect, useState } from "react";
import { message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import DateFilter from "../../../Components/DateFilter";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import { getAuditLogs } from "../../../API/FleetManager/AuditTrail";
import "./style.css";
import { getInitials } from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path) || path.startsWith("blob:")) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const ROLE_BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const ROLE_BADGE_BASE_STYLE = {
  padding: "5.5px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 500,
  display: "inline-block",
};

const getRoleBadgeStyle = (role) => {
  if (!role || role === "-") {
    return { ...ROLE_BADGE_BASE_STYLE, background: "#f4f4f5", color: "#71717a" };
  }
  return {
    ...ROLE_BADGE_BASE_STYLE,
    ...ROLE_BADGE_COLORS[hashString(role) % ROLE_BADGE_COLORS.length],
  };
};

const statusStyle = {
  Completed: { background: "#16a34a1f", color: "#16a34a" },
  Approved: { background: "#16a34a1f", color: "#16a34a" },
  Disabled: { background: "#dc26261f", color: "#dc2626" },
  Modified: { background: "#f59e0b1f", color: "#f59e0b" },
  Failed: { background: "#dc26261f", color: "#dc2626" },
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const PerformedByAvatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <span
        className="fm-audit-trail__performed-avatar"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background: getAvatarColor(name),
        }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="fm-audit-trail__performed-avatar"
      onError={() => setFailed(true)}
    />
  );
};

const AuditTrailManager = () => {
  const [dataSource, setDataSource] = useState([]);
  const { from, to } = useDateFilter();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getAuditLogs(startDate, endDate);
        const payload = res?.data?.data;
        const logs = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.logs)
            ? payload.logs
            : [];

        if (Math.floor(res?.status / 100) === 2) {
          setDataSource(
            logs.map((item, index) => ({
              key: String(item.id ?? index),
              actionTitle: item.action_title || "-",
              actionSubtitle: item.action_subtitle || "",
              subtitleColor: item.subtitle_color || "default",
              performedBy: item.performed_by_name || "-",
              avatar: resolveFileUrl(item.performed_by_avatar),
              role: item.role || "-",
              date: item.date || "-",
              status: capitalize(item.status) || "-",
            })),
          );
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load audit logs."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load audit logs."),
        );
      }
    };

    fetchData();
  }, [from, to]);

  const getUniqueFilters = (data, key) =>
    [...new Set(data.map((item) => item[key]))].map((value) => ({
      text: value,
      value,
    }));

  const roleFilters = getUniqueFilters(dataSource, "role").filter(
    (f) => f.value !== "-",
  );
  const statusFilters = getUniqueFilters(dataSource, "status");

  const renderSubtitle = (record) => {
    if (record.subtitleColor === "red") {
      return (
        <div className="fm-audit-trail__action-subtitle red">
          {record.actionSubtitle}
        </div>
      );
    }
    if (record.subtitleColor === "strikethrough") {
      const parts = record.actionSubtitle.split(" → ");
      return (
        <div className="fm-audit-trail__action-subtitle">
          <span className="fm-audit-trail__strikethrough-text">{parts[0]}</span>
          {" → "}
          <span>{parts[1]}</span>
        </div>
      );
    }
    if (record.subtitleColor === "chips") {
      const parts = record.actionSubtitle.split(" → ");
      return (
        <div className="fm-audit-trail__action-subtitle fm-audit-trail__chips-row">
          <span className="fm-audit-trail__chip fm-audit-trail__chip-old">
            {parts[0]}
          </span>
          <span className="fm-audit-trail__chip-arrow">→</span>
          <span className="fm-audit-trail__chip fm-audit-trail__chip-new">
            {parts[1]}
          </span>
        </div>
      );
    }
    return (
      <div className="fm-audit-trail__action-subtitle">
        {record.actionSubtitle}
      </div>
    );
  };

  const columns = [
    {
      title: "Action Description",
      key: "actionDescription",
      render: (_, record) => (
        <div className="fm-audit-trail__action-description-cell">
          <div className="fm-audit-trail__action-title">
            {record.actionTitle}
          </div>
          {renderSubtitle(record)}
        </div>
      ),
    },
    {
      title: "Performed By",
      key: "performedBy",
      enableSearch: true,
      render: (_, record) => (
        <div className="fm-audit-trail__performed-by-cell">
          <PerformedByAvatar src={record.avatar} name={record.performedBy} />
          <span className="fm-audit-trail__performed-name">
            {record.performedBy}
          </span>
        </div>
      ),
    },
    {
      title: "Role",
      key: "role",
      filters: roleFilters,
      onFilter: (value, record) => record.role === value,
      render: (_, record) => {
        if (record.role === "-") return record.role;

        const style = getRoleBadgeStyle(record.role);
        return (
          <span className="fuel-type-badge" style={style}>
            {record.role}
          </span>
        );
      },
    },
    {
      title: "Date & Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => {
        const parts = text.split(" ");
        const datePart = parts[0];
        const timePart = parts[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span
              style={{
                color: "#71717A",
              }}
            >
              {timePart}
            </span>
          </div>
        );
      },
    },

    {
      title: "Status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (_, record) => {
        const style = statusStyle[record.status] || {
          background: "#f4f4f5",
          color: "#6f7e93",
        };
        return (
          <span className="status-badge" style={style}>
            {record.status}
          </span>
        );
      },
    },
  ];

  return (
    <div className="fm-audit-trail">
      <div className="fm-audit-trail__controls">
        <DateFilter />
      </div>
      <GenericTable
        title="Audit Trail & Activity History"
        dataSource={dataSource}
        columns={columns}
        enableRowSelection={false}
      />
    </div>
  );
};

export default AuditTrailManager;
